"use client";

import Image from "next/image";
import React from "react";

interface MarqueeImage {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

interface ImageMarqueeProps {
  images: MarqueeImage[];
  speed?: number; // animation duration (seconds)
  className?: string;
  direction?: "left" | "right"; // animation direction
  pauseOnHover?: boolean; // pause animation on hover
  height?: string; // custom height
  imageClassName?: string; // custom image styling
}

const ImageMarquee: React.FC<ImageMarqueeProps> = ({
  images,
  speed = 20,
  className = "",
  direction = "left",
  pauseOnHover = true,
  height,
  imageClassName = "",
}) => {
  // Duplicate images for infinite loop
  const duplicatedImages = [...images, ...images];

  const marqueeStyles = `
    .marquee-track {
      display: flex;
      animation: scroll-${direction} ${speed}s linear infinite;
      ${pauseOnHover ? 'animation-play-state: running;' : ''}
    }
    
    .marquee-container:hover .marquee-track {
      ${pauseOnHover ? 'animation-play-state: paused;' : ''}
    }

    @keyframes scroll-left {
      0% {
        transform: translateX(0%);
      }
      100% {
        transform: translateX(-50%);
      }
    }

    @keyframes scroll-right {
      0% {
        transform: translateX(-50%);
      }
      100% {
        transform: translateX(0%);
      }
    }
  `;

  // Default height classes if no custom height provided
  const defaultHeightClasses = "h-[200px] sm:h-[250px] md:h-[350px] lg:h-[450px] xl:h-[550px]  ";
  const containerHeight = height || defaultHeightClasses;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: marqueeStyles }} />

      <div
        className={`marquee-container relative w-full overflow-hidden ${className} ${containerHeight}`}
      >
        <div className="marquee-track w-[200%]">
          {duplicatedImages.map((img, idx) => (
            <div
              key={idx}
              className={`flex-shrink-0 px-2 sm:px-3 md:px-4 lg:px-5 ${containerHeight}`}
            >
              <Image
                src={img.src}
                alt={img.alt}
                width={img.width || 1200}
                height={img.height || 600}
                className={`w-full xl:w-auto h-full object-fill   ${imageClassName}`}
              />
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ImageMarquee;
